import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Bot,
  CheckCircle,
  GitBranch,
  KeyRound,
  ScrollText,
  ShieldCheck,
  Workflow,
  XCircle,
} from 'lucide-react';
import type { DaemonReadinessReportResult, DaemonRecord, DaemonRunRecord } from '../../../generated/api/types.gen.js';
import { Badge, Button, Callout, Card, DataTable, StatCard, describeQueryError } from '../../../components/ui/index.js';
import type { BadgeVariant, DataTableColumn } from '../../../components/ui/index.js';
import { Can } from '../../../components/auth/Can.js';
import { humanize } from '../admin-format.js';
import { daemonReads } from './daemon-queries.js';
import { useDaemonScope } from './DaemonScopeContext.js';

interface DaemonOverviewTabProps {
  daemon: DaemonRecord;
  onRunNow: () => void;
  onResetGovernance: () => void;
  isRunning: boolean;
  isResettingGovernance: boolean;
}

const TREND_ICONS: Record<string, React.ReactNode> = {
  improving: <Activity size={16} />,
  stable: <CheckCircle size={16} />,
  declining: <XCircle size={16} />,
};

function runVariant(status: string | undefined): BadgeVariant {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'danger';
  return 'neutral';
}

const RECENT_RUN_COLUMNS: DataTableColumn<DaemonRunRecord>[] = [
  {
    key: 'status',
    header: 'Status',
    render: (run) => (
      <Badge variant={runVariant(run.status)} dot>
        {humanize(run.status ?? 'unknown')}
      </Badge>
    ),
  },
  {
    key: 'triggeredBy',
    header: 'Started by',
    render: (run) => <span className="text-sm text-muted">{humanize(run.triggeredBy ?? 'unknown')}</span>,
  },
  {
    key: 'findings',
    header: 'Findings',
    align: 'right',
    render: (run) => <span className="text-sm text-muted">{run.findingsCount ?? 0}</span>,
  },
  {
    key: 'duration',
    header: 'Duration',
    align: 'right',
    render: (run) => (
      <span className="text-sm text-muted">
        {run.durationMs != null ? `${Math.round(run.durationMs / 1000)} s` : 'Unknown'}
      </span>
    ),
  },
  {
    key: 'startedAt',
    header: 'Started',
    render: (run) => (
      <span className="whitespace-nowrap text-sm text-muted">
        {run.startedAt ? new Date(run.startedAt).toLocaleString() : 'Unknown'}
      </span>
    ),
  },
];

function formatReviewRun(run: Record<string, unknown> | null | undefined): string {
  if (!run) return 'No run recorded';
  const status = typeof run.status === 'string' ? humanize(run.status) : 'Unknown';
  return typeof run.startedAt === 'string' ? `${status} on ${new Date(run.startedAt).toLocaleDateString()}` : status;
}

function readinessLabel(readiness: DaemonReadinessReportResult | undefined): string {
  if (!readiness) return 'Checking';
  if (readiness.ready) return 'Ready';
  const count = readiness.blocking.length;
  return `${count} ${count === 1 ? 'blocker' : 'blockers'}`;
}

interface TileProps {
  icon: React.ReactNode;
  title: string;
  value: React.ReactNode;
  badge?: React.ReactNode;
  description: React.ReactNode;
  to?: string;
}

const Tile: React.FC<TileProps> = ({ icon, title, value, badge, description, to }) => {
  const body = (
    <div className="h-full rounded-xl border bg-surface-alt p-4 transition-colors hover:bg-surface-hover">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-heading">
          {icon}
          {title}
        </div>
        {badge ?? (to ? <ArrowRight size={14} className="text-muted" aria-hidden="true" /> : null)}
      </div>
      <div className="mt-3 text-lg font-semibold text-heading">{value}</div>
      <p className="mt-1 text-sm text-muted">{description}</p>
    </div>
  );
  return to ? (
    <Link to={to} className="block h-full">
      {body}
    </Link>
  ) : (
    body
  );
};

export const DaemonOverviewTab: React.FC<DaemonOverviewTabProps> = ({
  daemon,
  onRunNow,
  onResetGovernance,
  isRunning,
  isResettingGovernance,
}) => {
  const { scope, basePath, policy } = useDaemonScope();
  const reads = daemonReads(scope);

  const trendQuery = useQuery(reads.trend(daemon.id));
  const runsQuery = useQuery(reads.runs(daemon.id, { limit: '5' }));
  const readinessQuery = useQuery(reads.readiness(daemon.id));
  const workflowsQuery = useQuery(reads.workflows(daemon.id));

  const trend = trendQuery.data;
  const readiness = readinessQuery.data?.data;

  // Four independent widgets share this tab; a single QueryBoundary would hide the ones that did load, so a
  // failure is a non-blocking banner and each widget degrades on its own.
  const failed = [
    { query: trendQuery, label: 'the trend' },
    { query: runsQuery, label: 'recent runs' },
    { query: readinessQuery, label: 'system health' },
    { query: workflowsQuery, label: 'workflow collaboration' },
  ].filter((entry) => entry.query.isError);

  const recentRuns = runsQuery.data?.data ?? [];
  const latestRun = recentRuns[0] ?? null;
  const assignments = workflowsQuery.data?.data ?? [];
  const enabledWorkflows = assignments.filter((a) => a.enabled !== false).length;
  const primaryWorkflow = assignments.find((a) => (a.purpose ?? 'primary') === 'primary');
  const qualityScore = daemon.qualityScore ? (Number.parseFloat(daemon.qualityScore) * 100).toFixed(0) : null;
  const consecutiveFailures = Number(daemon.consecutiveFailures ?? 0);
  const circuitBroken = consecutiveFailures >= 5;
  const openHighFindings = readiness?.selfReview.openHighFindings ?? 0;
  const activeGrants = readiness?.permissionGrantUsage.active ?? 0;
  const staleGrants = readiness?.permissionGrantUsage.staleUnusedAlways ?? 0;
  const detailPath = `${basePath}/${daemon.id}`;
  const trendKey = trend?.trend ?? 'stable';

  return (
    <div className="space-y-5">
      {failed.length > 0 && (
        <Callout tone="danger" icon={AlertTriangle} title="Part of this page did not load">
          Could not load {failed.map((entry) => entry.label).join(', ')}:{' '}
          {describeQueryError(failed[0].query.error, failed[0].label)}
        </Callout>
      )}

      {circuitBroken && (
        <Callout
          tone="warning"
          icon={AlertTriangle}
          title="The failure circuit is open"
          actions={
            <Can policy={policy}>
              <Button size="sm" variant="secondary" loading={isResettingGovernance} onClick={onResetGovernance}>
                Reset circuit
              </Button>
            </Can>
          }
        >
          This {scope === 'system' ? 'daemon' : 'employee'} has failed {consecutiveFailures} runs in a row. Reset the
          circuit after fixing the underlying issue, then run it again.
        </Callout>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Total runs" value={daemon.runCount ?? 0} icon={<Activity size={16} />} />
        <StatCard label="Findings" value={daemon.totalFindingsCount ?? 0} icon={<AlertTriangle size={16} />} />
        <StatCard
          label="Quality"
          value={qualityScore ? `${qualityScore}%` : 'None yet'}
          icon={<CheckCircle size={16} />}
        />
        <StatCard label="Trend" value={humanize(trendKey)} icon={TREND_ICONS[trendKey] ?? TREND_ICONS.stable} />
      </div>

      <Card
        title="System health"
        actions={
          <Link to={`${detailPath}?tab=health`} className="inline-flex items-center gap-1 text-sm text-accent-text">
            Health details <ArrowRight size={13} aria-hidden="true" />
          </Link>
        }
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <Tile
            icon={<ShieldCheck size={16} aria-hidden="true" />}
            title="Automation readiness"
            value={daemon.enabled ? readinessLabel(readiness) : 'Disabled'}
            badge={
              <Badge variant={!daemon.enabled ? 'neutral' : readiness?.ready ? 'success' : 'warning'}>
                {!daemon.enabled ? 'Off' : readiness?.ready ? 'Ready' : 'Blocked'}
              </Badge>
            }
            description={
              !daemon.enabled
                ? 'Automation is switched off on purpose.'
                : readiness?.ready
                  ? 'Ready for scheduled and manual runs.'
                  : (readiness?.blocking[0]?.message ?? 'Readiness checks are still loading.')
            }
          />
          <Tile
            icon={<AlertTriangle size={16} aria-hidden="true" />}
            title="Self-review"
            value={`${openHighFindings} open high or critical`}
            badge={<Badge variant={openHighFindings > 0 ? 'danger' : 'success'}>Findings</Badge>}
            description={`Security: ${formatReviewRun(readiness?.selfReview.lastSecurityRun)}. Quality: ${formatReviewRun(readiness?.selfReview.lastQualityRun)}.`}
          />
          <Tile
            icon={<KeyRound size={16} aria-hidden="true" />}
            title="Permission grants"
            value={`${activeGrants} active`}
            badge={<Badge variant={staleGrants > 0 ? 'warning' : activeGrants > 0 ? 'info' : 'neutral'}>Policy</Badge>}
            description={
              staleGrants > 0
                ? `${staleGrants} long-lived grants have not been used recently.`
                : activeGrants > 0
                  ? 'Active grants are visible and reversible in the run audit.'
                  : 'No active scoped grants.'
            }
          />
          <Tile
            icon={<ScrollText size={16} aria-hidden="true" />}
            title="Latest audit"
            value={latestRun ? humanize(latestRun.status ?? 'unknown') : 'No runs yet'}
            badge={
              <Badge variant={latestRun ? runVariant(latestRun.status) : 'neutral'}>
                {latestRun ? humanize(latestRun.triggeredBy ?? 'run') : 'Audit'}
              </Badge>
            }
            description={
              latestRun?.startedAt
                ? `${new Date(latestRun.startedAt).toLocaleString()}${latestRun.findingsCount != null ? `, ${latestRun.findingsCount} findings` : ''}`
                : 'Run it to create an auditable execution trail.'
            }
            to={latestRun ? `${detailPath}?tab=runs&runId=${latestRun.id}` : `${detailPath}?tab=runs`}
          />
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Tile
          icon={<Workflow size={16} aria-hidden="true" />}
          title="Workflow collaboration"
          value={`${enabledWorkflows}/${assignments.length} enabled`}
          description={primaryWorkflow ? 'A primary workflow is assigned.' : 'No primary workflow assignment found.'}
          to={`${detailPath}?tab=workflow`}
        />
        <Tile
          icon={<GitBranch size={16} aria-hidden="true" />}
          title="Supervision"
          value={daemon.supervisorDaemonId ? 'Supervised' : 'Human review'}
          description={
            daemon.probationUntil
              ? `On probation until ${new Date(daemon.probationUntil).toLocaleDateString()}.`
              : 'Open the supervision tab to inspect review relationships.'
          }
          to={`${detailPath}?tab=supervision`}
        />
        <Tile
          icon={<AlertTriangle size={16} aria-hidden="true" />}
          title="Findings"
          value={`${daemon.totalFindingsCount ?? 0} total`}
          description={
            openHighFindings > 0
              ? `${openHighFindings} high or critical findings need review.`
              : 'No high or critical self-review findings.'
          }
          to={`${detailPath}?tab=findings`}
        />
      </div>

      <Card title="Profile" actions={<Bot size={16} className="text-muted" aria-hidden="true" />}>
        <dl className="grid grid-cols-2 gap-4 text-sm lg:grid-cols-4">
          <div>
            <dt className="text-muted">Department</dt>
            <dd className="text-body">{humanize(daemon.department ?? 'custom')}</dd>
          </div>
          <div>
            <dt className="text-muted">Permission tier</dt>
            <dd>
              <Badge variant="neutral">{humanize(daemon.permissionTier ?? 'observer')}</Badge>
            </dd>
          </div>
          <div>
            <dt className="text-muted">Max runs per day</dt>
            <dd className="text-body">{daemon.maxRunsPerDay ?? 10}</dd>
          </div>
          <div>
            <dt className="text-muted">Hired</dt>
            <dd className="text-body">{daemon.hiredAt ? new Date(daemon.hiredAt).toLocaleDateString() : 'Not set'}</dd>
          </div>
        </dl>
        {daemon.mandate && (
          <div className="mt-4 border-t pt-3">
            <p className="mb-1 text-sm text-muted">Mandate</p>
            <p className="text-sm text-body">{daemon.mandate}</p>
          </div>
        )}
      </Card>

      <Can policy={policy}>
        <div>
          <Button onClick={onRunNow} disabled={!daemon.enabled} loading={isRunning}>
            Run now
          </Button>
        </div>
      </Can>

      {recentRuns.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-lg font-semibold text-heading">Recent runs</h3>
          <DataTable
            framed
            columns={RECENT_RUN_COLUMNS}
            rows={recentRuns}
            rowKey={(run) => run.id}
            aria-label="Recent runs"
          />
        </section>
      )}
    </div>
  );
};
